<!-- eslint-disable vue/no-unused-vars -->
<template>
  <div class="modern-dashboard">
    <!-- Top Header with Brand Identity -->
    <div class="dashboard-header">
      <div class="header-content">
        <div class="brand-section">
          <img
            src="@/assets/images/dashboard/bolt-3d.png"
            alt="Dropway"
            class="brand-logo-img"
          >
          <div class="brand-info">
            <h1 class="brand-title">
              Bienvenue, DropWay
            </h1>
            <p class="brand-subtitle">
              Voici un aperçu de vos performances aujourd'hui
            </p>
          </div>
        </div>

        <div class="header-actions">
          <div class="time-display">
            <feather-icon
              icon="ClockIcon"
              size="16"
              class="time-icon"
            />
            <span class="current-time">{{ getCurrentTime() }}</span>
          </div>
          <b-button
            variant="outline-light"
            size="sm"
            class="refresh-btn"
            :disabled="isLoading"
            @click="refreshData"
          >
            <feather-icon
              :icon="isLoading ? 'RefreshCwIcon' : 'RefreshCwIcon'"
              size="16"
              :class="{ 'spinning': isLoading }"
            />
            {{ isLoading ? 'Chargement...' : 'Actualiser' }}
          </b-button>
        </div>
      </div>
    </div>

    <!-- Info Notice Section - COMMENTED OUT -->
    <!--
    <div class="info-notice-container">
      <div class="info-notice">
        <div class="notice-icon">
          <feather-icon
            icon="InfoIcon"
            size="20"
            class="icon"
          />
        </div>
        <div class="notice-content">
          <p class="notice-text">
            حرصا منا على توفير المعلوم المتفق عليه في الأجل المحدد ، نعلم حرفاءنا الكرام أنه سيتم اقتطاع 1د كمعلوم بسيط على كل فاتورة خلاص كما نعدكم بمزيد التألق والنجاح.
          </p>
          <p
            v-if="$branding.features.showSlogan && $branding.company.slogan"
            class="notice-tagline"
          >
            {{ $branding.company.shortName }} : {{ $branding.company.slogan }}
          </p>
        </div>
      </div>
    </div>
    -->

    <br>
    <DashboardBanners mode="display" />

    <!-- Loading Overlay -->
    <div
      v-if="isLoading"
      class="loading-overlay"
    >
      <div class="loading-content">
        <div class="loading-spinner" />
        <p class="loading-text">
          Chargement des statistiques...
        </p>
      </div>
    </div>

    <!-- Statistics Grid -->
    <div class="stats-container">
      <div
        v-if="todayStatusConfigs.length"
        class="today-stats-grid"
      >
        <div
          v-for="statusConfig in todayStatusConfigs"
          :key="statusConfig.status"
          class="today-card-wrapper"
          @click="show(statusConfig.status)"
        >
          <div
            class="today-stat-card-inner"
            :class="statusConfig.class"
          >
            <div class="today-card-header">
              <div class="card-left">
                <div class="card-icon-wrapper">
                  <feather-icon
                    :icon="statusConfig.icon"
                    size="28"
                    class="card-icon"
                  />
                </div>
                <div>
                  <h3 class="card-title-inline">
                    {{ statusConfig.title }}
                  </h3>
                  <p class="today-card-description">
                    {{ statusConfig.description }}
                  </p>
                </div>
              </div>
              <span class="today-badge">
                Aujourd'hui
              </span>
            </div>
            <div class="today-stats-row">
              <div class="today-stat-block">
                <small>N° colis</small>
                <h2>
                  {{ formatNumber(getStatusData(statusConfig.status).number) }}
                </h2>
              </div>
              <div class="today-stat-separator" />
              <div class="today-stat-block">
                <small>Montant</small>
                <p>
                  {{ formatCurrency(getStatusData(statusConfig.status).total_price_ttc) }}
                </p>
              </div>
            </div>

            <div class="card-footer">
              <div class="progress-bar">
                <div
                  class="progress-fill"
                  :style="{ width: getProgressWidth(statusConfig.status) + '%' }"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="stats-grid">
        <!-- Status Cards -->
        <div
          v-for="statusConfig in otherStatusConfigs"
          :key="statusConfig.status"
          class="stat-card-container"
          @click="show(statusConfig.status)"
        >
          <div
            class="stat-card"
            :class="statusConfig.class"
          >
            <div class="card-header">
              <div class="card-left">
                <div class="card-icon-wrapper">
                  <feather-icon
                    :icon="statusConfig.icon"
                    size="24"
                    class="card-icon"
                  />
                </div>
                <div class="card-title-header">
                  <h3 class="card-title-inline">
                    {{ statusConfig.title }}
                  </h3>
                </div>
              </div>

            </div>

            <div class="card-content">
              <div class="card-stats">
                <div class="stat-number-row">
                  <p class="stat-label">
                    N° colis :
                  </p>
                  <h2 class="stat-number">
                    {{ formatNumber(getStatusData(statusConfig.status).number) }}
                  </h2>
                </div>
                <div class="stat-amount-row">
                  <p class="stat-label">
                    Prix :
                  </p>
                  <p class="stat-amount">
                    {{ formatCurrency(getStatusData(statusConfig.status).total_price_ttc) }}
                  </p>
                </div>
              </div>
            </div>

            <div class="card-footer">
              <div class="progress-bar">
                <div
                  class="progress-fill"
                  :style="{ width: getProgressWidth(statusConfig.status) + '%' }"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Stats Summary -->
    <div class="summary-section">
      <b-card class="summary-card">
        <div class="summary-content">
          <div class="summary-item">
            <feather-icon
              icon="PackageIcon"
              size="20"
              class="summary-icon"
            />
            <div class="summary-data">
              <h4 class="summary-number">
                {{ getTotalOrders() }}
              </h4>
              <p class="summary-label">
                Total Commandes
              </p>
            </div>
          </div>

          <div class="summary-divider" />

          <div class="summary-item">
            <feather-icon
              icon="DollarSignIcon"
              size="20"
              class="summary-icon success"
            />
            <div class="summary-data">
              <h4 class="summary-number">
                {{ formatCurrency(getTotalRevenue()) }}
              </h4>
              <p class="summary-label">
                Chiffre d'Affaires
              </p>
            </div>
          </div>

          <div class="summary-divider" />

          <div class="summary-item">
            <feather-icon
              icon="TrendingUpIcon"
              size="20"
              class="summary-icon warning"
            />
            <div class="summary-data">
              <h4 class="summary-number">
                {{ getDeliveryRate() }}%
              </h4>
              <p class="summary-label">
                Taux de Livraison
              </p>
            </div>
          </div>

          <div class="summary-divider" />

          <div class="summary-item">
            <feather-icon
              icon="TrendingDownIcon"
              size="20"
              class="summary-icon error"
            />
            <div class="summary-data">
              <h4 class="summary-number">
                {{ getReturnRate() }}%
              </h4>
              <p class="summary-label">
                Taux de Retour
              </p>
            </div>
          </div>

          <div class="summary-divider" />

          <div class="summary-item">
            <feather-icon
              icon="ClockIcon"
              size="20"
              class="summary-icon info"
            />
            <div class="summary-data">
              <h4 class="summary-number">
                {{ getPendingOrders() }}
              </h4>
              <p class="summary-label">
                En Attente
              </p>
            </div>
          </div>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
} from 'bootstrap-vue'
import axios from 'axios'
import storeAuth from '@/store/store'

export default {
  name: 'Dashboard',
  components: {
    BCard,
    BButton,
  },
  data() {
    return {
      result: [],
      dashboardBanners: [],
      isLoading: false,
      user: {},
      currentTime: new Date(),
      statusConfigs: [
        {
          status: 0,
          title: 'Colis En Attente',
          description: 'Colis en attente',
          icon: 'ClockIcon',
          trendIcon: 'TrendingUpIcon',
          trendClass: 'trend-neutral',
          class: 'pending-status',
        },
        {
          status: 15,
          title: 'Colis À Enlever',
          description: 'Colis à enlever',
          icon: 'PackageIcon',
          trendIcon: 'ClockIcon',
          trendClass: 'trend-neutral',
          class: 'attente-collecte-status',
        },
        {
          status: 1,
          title: 'Colis Enlevés',
          description: 'Colis enlevés',
          icon: 'CheckCircleIcon',
          trendIcon: 'ArrowUpIcon',
          trendClass: 'trend-positive',
          class: 'collected-status',
        },
        {
          status: 2,
          title: 'Colis Au Dépôt',
          description: 'Colis au dépôt',
          icon: 'HomeIcon',
          trendIcon: 'DownloadIcon',
          trendClass: 'trend-neutral',
          class: 'depot-status',
        },
        {
          status: 5,
          title: 'Retour au Dépôt',
          description: 'Retour au dépôt',
          icon: 'RotateCcwIcon',
          trendIcon: 'AlertTriangleIcon',
          trendClass: 'trend-warning',
          class: 'return-status',
        },
        {
          status: 3,
          title: 'En Cours de livraison',
          description: 'Livraison en cours',
          icon: 'TruckIcon',
          trendIcon: 'NavigationIcon',
          trendClass: 'trend-active',
          class: 'en-cours-status',
        },
        {
          status: 4,
          title: 'Livré',
          description: 'Livraison réussie',
          icon: 'CheckSquareIcon',
          trendIcon: 'CheckIcon',
          trendClass: 'trend-positive',
          class: 'delivered-status',
        },
        {
          status: 6,
          title: 'Livré & Payé',
          description: 'Processus terminé',
          icon: 'CreditCardIcon',
          trendIcon: 'DollarSignIcon',
          trendClass: 'trend-positive',
          class: 'completed-status',
        },
        {
          status: 22,
          title: 'Retour définitif',
          description: 'Retour définitif confirmé',
          icon: 'XOctagonIcon',
          trendIcon: 'AlertCircleIcon',
          trendClass: 'trend-negative',
          class: 'retour-definitif-status',
        },
        {
          status: 7,
          title: 'Retour expéditeur',
          description: 'Retour fournisseur',
          icon: 'XCircleIcon', // Retour expéditeur
          trendIcon: 'AlertCircleIcon',
          trendClass: 'trend-negative',
          class: 'final-return-status',
        },
        {
          status: 10,
          title: 'rtn client-agence',
          description: 'retour inter agency',
          icon: 'RotateCcwIcon',
          trendIcon: 'AlertCircleIcon',
          trendClass: 'trend-negative',
          class: 'return-inter-agency-status',
        },
        // {
        //   status: 11,
        //   title: 'rtn client-agence recu',
        //   description: 'return inter agency received',
        //   icon: 'CheckCircleIcon',
        //   trendIcon: 'AlertCircleIcon',
        //   trendClass: 'trend-negative',
        //   class: 'return-inter-agency-received-status',
        // },
        {
          status: 9,
          title: 'Retour Payé',
          description: 'Paiement retour',
          icon: 'FileTextIcon',
          trendIcon: 'DollarSignIcon',
          trendClass: 'trend-warning',
          class: 'return-billed-status',
        },
        {
          status: 19,
          title: 'Livré Temporaire',
          description: 'En attente de validation dépôt',
          icon: 'CheckCircleIcon',
          trendIcon: 'ClockIcon',
          trendClass: 'trend-warning',
          class: 'livre-temporaire-status',
        },
        {
          status: 20,
          title: 'Problème livraison',
          description: 'À revérifier au dépôt',
          icon: 'RotateCwIcon',
          trendIcon: 'ClockIcon',
          trendClass: 'trend-warning',
          class: 'retour-temporaire-status',
        },
        // {
        //   status: 8,
        //   title: 'En Transfert',
        //   description: 'Transfert en cours',
        //   icon: 'RefreshCwIcon',
        //   trendIcon: 'ArrowRightCircleIcon',
        //   trendClass: 'trend-active',
        //   class: 'transfer-status',
        // },
        // {
        //   status: 12,
        //   title: 'Annulé',
        //   description: 'Commande annulée',
        //   icon: 'XIcon',
        //   trendIcon: 'StopCircleIcon',
        //   trendClass: 'trend-negative',
        //   class: 'cancelled-status',
        // },
        {
          status: 14,
          title: 'À vérifier',
          description: 'Nécessitent vérification',
          icon: 'AlertTriangleIcon',
          trendIcon: 'SearchIcon',
          trendClass: 'trend-warning',
          class: 'verify-status',
        },
        {
          status: 18,
          title: 'Rendez Vous',
          description: 'Rendez Vous',
          icon: 'CalendarIcon',
          trendIcon: 'ClockIcon',
          trendClass: 'trend-neutral',
          class: 'planning-status',
        },
        {
          status: 24,
          title: 'Anomalie livraison',
          description: 'Anomalie de livraison',
          icon: 'AlertOctagonIcon',
          trendIcon: 'AlertTriangleIcon',
          trendClass: 'trend-negative',
          class: 'delivery-anomaly-status',
        },
        // {
        //   status: 16,
        //   title: 'À Rembourser',
        //   description: 'En attente de remboursement',
        //   icon: 'DollarSignIcon',
        //   trendIcon: 'ClockIcon',
        //   trendClass: 'trend-warning',
        //   class: 'a-rembourser-status',
        // },
        // {
        //   status: 17,
        //   title: 'Remboursé',
        //   description: 'Remboursement effectué',
        //   icon: 'CheckCircleIcon',
        //   trendIcon: 'CheckIcon',
        //   trendClass: 'trend-positive',
        //   class: 'rembourse-status',
        // },
      ],
    }
  },
  computed: {
    todayStatusConfigs() {
      const todayStatuses = [19, 20]
      return this.statusConfigs
        .filter(cfg => todayStatuses.includes(cfg.status))
        .filter(cfg => {
          const data = this.getStatusData(cfg.status)
          return data && data.number > 0
        })
    },
    otherStatusConfigs() {
      const todayStatuses = new Set([19, 20])
      const captureOrder = [0, 15, 1, 2, 5, 3, 14, 4, 6, 22, 10, 7, 9, 18, 24]
      const items = this.statusConfigs
        .filter(cfg => !todayStatuses.has(cfg.status))

      return items.sort((a, b) => {
        const ai = captureOrder.indexOf(a.status)
        const bi = captureOrder.indexOf(b.status)
        if (ai === -1 && bi === -1) return 0
        if (ai === -1) return 1
        if (bi === -1) return -1
        return ai - bi
      })
    },
    statusItems() {
      if (!this.result || this.result.length === 0) {
        return []
      }

      return [
        {
          title: 'En attente de collecte',
          subtitle: 'Expéditions en attente',
          count: this.result.find(item => item.status === 0)?.number || 0,
          color: 'warning',
          icon: 'ClockIcon',
          status: 0,
        },
        {
          title: 'Collectées',
          subtitle: 'Expéditions collectées',
          count: this.result.find(item => item.status === 1)?.number || 0,
          color: 'info',
          icon: 'TruckIcon',
          status: 1,
        },
        {
          title: 'En transit',
          subtitle: 'Centre de tri',
          count: this.result.find(item => item.status === 2)?.number || 0,
          color: 'primary',
          icon: 'PackageIcon',
          status: 2,
        },
        {
          title: 'En livraison',
          subtitle: 'Livraison en cours',
          count: this.result.find(item => item.status === 3)?.number || 0,
          color: 'info',
          icon: 'TruckIcon',
          status: 3,
        },
        {
          title: 'Livrées avec succès',
          subtitle: 'Livraisons réussies',
          count: this.result.find(item => item.status === 4)?.number || 0,
          color: 'success',
          icon: 'CheckCircleIcon',
          status: 4,
        },
        {
          title: 'Retour centre',
          subtitle: 'Échec de livraison',
          count: this.result.find(item => item.status === 5)?.number || 0,
          color: 'warning',
          icon: 'RotateCcwIcon',
          status: 5,
        },
        {
          title: 'Confirmées et payées',
          subtitle: 'Livraisons payées',
          count: this.result.find(item => item.status === 6)?.number || 0,
          color: 'success',
          icon: 'DollarSignIcon',
          status: 6,
        },
        {
          title: 'Retour expéditeur',
          subtitle: 'Retour expéditeur',
          count: this.result.find(item => item.status === 7)?.number || 0,
          color: 'danger',
          icon: 'ArrowLeftIcon',
          status: 7,
        },
        {
          title: 'Livré Temporaire',
          subtitle: 'Validations dépôt en attente',
          count: this.result.find(item => item.status === 19)?.number || 0,
          color: 'success',
          icon: 'CheckCircleIcon',
          status: 19,
        },
        {
          title: 'Problème livraison',
          subtitle: 'Suivi fin de journée',
          count: this.result.find(item => item.status === 20)?.number || 0,
          color: 'warning',
          icon: 'RotateCwIcon',
          status: 20,
        },
        {
          title: 'rtn client-agence',
          subtitle: 'En transfert inter-agence',
          count: this.result.find(item => item.status === 10)?.number || 0,
          color: 'danger',
          icon: 'RotateCcwIcon',
          status: 10,
        },
        {
          title: 'rtn client-agence recu',
          subtitle: 'Reçu en inter-agence',
          count: this.result.find(item => item.status === 11)?.number || 0,
          color: 'danger',
          icon: 'CheckCircleIcon',
          status: 11,
        },
        // {
        //   title: 'Transfert inter-centres',
        //   subtitle: 'En transfert',
        //   count: this.result.find(item => item.status === 8)?.number || 0,
        //   color: 'secondary',
        //   icon: 'ArrowRightIcon',
        //   status: 8,
        // },
        {
          title: 'Retours payés',
          subtitle: 'Retours payés',
          count: this.result.find(item => item.status === 9)?.number || 0,
          color: 'danger',
          icon: 'DollarSignIcon',
          status: 9,
        },
        {
          title: 'Prêtes pour collecte',
          subtitle: 'Disponibles collecte',
          count: this.result.find(item => item.status === 15)?.number || 0,
          color: 'info',
          icon: 'ClockIcon',
          status: 15,
        },
        {
          title: 'À vérifier',
          subtitle: 'Nécessitent vérification',
          count: this.result.find(item => item.status === 14)?.number || 0,
          color: 'warning',
          icon: 'AlertTriangleIcon',
          status: 14,
        },
        {
          title: 'Rendez Vous',
          subtitle: 'Rendez Vous',
          count: this.result.find(item => item.status === 18)?.number || 0,
          color: 'info',
          icon: 'CalendarIcon',
          status: 18,
        },
        // {
        //   title: 'À Rembourser',
        //   subtitle: 'En attente de remboursement',
        //   count: this.result.find(item => item.status === 16)?.number || 0,
        //   color: 'warning',
        //   icon: 'DollarSignIcon',
        //   status: 16,
        // },
        // {
        //   title: 'Remboursé',
        //   subtitle: 'Remboursement effectué',
        //   count: this.result.find(item => item.status === 17)?.number || 0,
        //   color: 'info',
        //   icon: 'CheckCircleIcon',
        //   status: 17,
        // },
      ] // Show all statuses (temporarily for testing)
      // .filter(item => item.count > 0) // Only show statuses that have orders
    },
  },
  created() {
    this.user = storeAuth.state.user
    // Apply default filter on load
    this.loadInitialData()
  },
  mounted() {
    this.updateTime()
    setInterval(this.updateTime, 1000)
  },
  methods: {
    async loadInitialData() {
      try {
        await this.getOrders()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading initial data:', error)
      }
    },

    show(level) {
      this.$router.push({
        name: 'order-list',
        params: { level: level.toString() },
      })
    },

    getStatusData(status) {
      return this.result.find(item => item.status === status) || { number: 0, total_price_ttc: 0 }
    },

    async getOrders() {
      this.isLoading = true
      let supplier = null
      if (this.user.role === 'supplier') {
        supplier = this.user.id
      }

      // Build query params for GET request
      const params = {}
      if (supplier) params.supplier = supplier
      if (this.user.repository) params.repository = this.user.repository

      try {
        const response = await axios.get('/api/orders/dashboard/', { params })
        this.result = response.data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching dashboard data:', error)
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Erreur',
            text: 'Erreur lors du chargement des données',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.isLoading = false
      }
    },

    refreshData() {
      this.getOrders()
    },

    updateTime() {
      this.currentTime = new Date()
    },

    getCurrentTime() {
      return this.currentTime.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    },

    formatNumber(num) {
      return new Intl.NumberFormat('fr-FR').format(num || 0)
    },

    formatCurrency(amount) {
      // Handle NaN, null, undefined, and non-numeric values
      const numericAmount = parseFloat(amount) || 0
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'TND',
        minimumFractionDigits: 0,
      }).format(numericAmount)
    },

    getTotalOrders() {
      return this.result.reduce((sum, item) => sum + (item.number || 0), 0)
    },

    getTotalRevenue() {
      return this.result.reduce((sum, item) => {
        const price = parseFloat(item.total_price_ttc) || 0
        return sum + price
      }, 0)
    },

    getStatusCount(status) {
      return this.getStatusData(status)?.number || 0
    },

    getDeliveredOrdersCount() {
      // Closed successful outcomes only.
      const deliveredStatuses = [4, 6, 19, 26]
      return deliveredStatuses.reduce((sum, status) => sum + this.getStatusCount(status), 0)
    },

    getReturnedOrdersCount() {
      // Closed failed/return outcomes only; excludes in-progress statuses.
      const returnedStatuses = [5, 7, 9, 20, 22, 24, 28]
      return returnedStatuses.reduce((sum, status) => sum + this.getStatusCount(status), 0)
    },

    getCompletedOrdersCount() {
      return this.getDeliveredOrdersCount() + this.getReturnedOrdersCount()
    },

    getDeliveryRate() {
      const delivered = this.getDeliveredOrdersCount()
      const completed = this.getCompletedOrdersCount()
      return completed > 0 ? Math.round((delivered / completed) * 100) : 0
    },

    getReturnRate() {
      const returned = this.getReturnedOrdersCount()
      const completed = this.getCompletedOrdersCount()
      return completed > 0 ? Math.round((returned / completed) * 100) : 0
    },

    getPendingOrders() {
      return (this.getStatusData(0)?.number || 0) + (this.getStatusData(1)?.number || 0)
    },

    getProgressWidth(status) {
      const data = this.getStatusData(status)
      if (!data) return 0
      const total = this.getTotalOrders()
      return total > 0 ? Math.min((data.number / total) * 100, 100) : 0
    },
  },
}
</script>

<style lang="scss" scoped>
.modern-dashboard {
  min-height: 100vh;
  background: #ffffff;
  font-family: 'Inter', 'Segoe UI', sans-serif;
}

// Header Section
.dashboard-header {
  position: relative;
  background:
    radial-gradient(circle at 11% 28%, rgba(255, 255, 255, 0.22) 0 9%, transparent 10%),
    radial-gradient(circle at 86% 18%, rgba(255, 255, 255, 0.14) 0 7%, transparent 8%),
    linear-gradient(135deg, #0b2d66 0%, #114092 46%, #1f6fd0 100%);
  color: white;
  padding: 2.5rem 0;
  box-shadow: 0 14px 40px rgba(17, 64, 146, 0.22);
  border-radius: 0 0 18px 18px;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, rgba(5, 19, 45, 0.28) 0%, rgba(5, 19, 45, 0) 62%);
    pointer-events: none;
  }

  .header-content {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .brand-section {
    display: flex;
    align-items: center;
    gap: 1.5rem;

    .brand-icon {
      width: 64px;
      height: 64px;
      background: transparent;
      border-radius: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      z-index: 1;

      .brand-logo {
        color: #ffd166;
        filter: drop-shadow(0 0 10px rgba(255, 214, 102, 0.8));
        animation: boltFloat 3s ease-in-out infinite, boltGlow 1.8s ease-in-out infinite;
      }

      &::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: inherit;
        background: radial-gradient(circle at 30% 30%, rgba(255, 214, 102, 0.35), transparent 55%);
        filter: blur(6px);
        animation: boltPulse 2.6s ease-in-out infinite;
      }
    }

    .brand-info {
      .brand-title {
        font-size: 2rem;
        font-weight: 700;
        margin: 0;
        color: #ffffff;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
      }

      .brand-subtitle {
        margin: 0;
        opacity: 0.9;
        font-size: 1.1rem;
        color: #ffffff;
      }
    }
  }

    }

  .info-notice-container {
    max-width: 1400px;
    margin: 2rem auto;
    padding: 0 2rem;

    .info-notice {
      background-color: #ffffff;
      background-image: url('/images/annonce-bg.png');
      background-position: center;
      background-repeat: no-repeat;
      background-size: cover;
      border-radius: 18px;
      padding: 1.75rem 2rem;
      box-shadow: 0 14px 45px rgba(0, 0, 0, 0.08);
      border: 1px solid #e5e7eb;
      display: flex;
      align-items: flex-start;
      gap: 1.5rem;
      position: relative;
      overflow: hidden;
      backdrop-filter: blur(10px);

      &::after {
        content: '';
        position: absolute;
        inset: 0;
        background: rgba(255, 255, 255, 0.78);
        pointer-events: none;
        z-index: 0;
      }

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 6px;
        height: 100%;
        background: #dd3938;
        border-radius: 6px;
        box-shadow: 0 0 15px rgba(221, 57, 56, 0.35);
        z-index: 1;
      }

      .notice-icon {
        position: relative;
        z-index: 2;
        width: 50px;
        height: 50px;
        background: linear-gradient(135deg, #ffedd5 0%, #ffe4e0 100%);
        border-radius: 14px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        box-shadow: 0 10px 25px rgba(221, 57, 56, 0.25);

        .icon {
          color: #dd3938;
        }
      }

      .notice-content {
        position: relative;
        z-index: 2;
        flex: 1;
        text-align: right;
        direction: rtl;

        .notice-text {
          margin: 0 0 1rem;
          font-size: 1.3rem;
          line-height: 1.8;
          color: #1f2937;
          font-weight: 600;
          letter-spacing: 0.3px;
        }

        .notice-tagline {
          margin: 0;
          font-size: 1.4rem;
          font-weight: 700;
          color: #dd3938;
          padding: 0.5rem 1rem;
          background: rgba(221, 57, 56, 0.08);
          border-radius: 10px;
          border-right: 4px solid #dd3938;
          text-align: center;
        }
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 15px 50px rgba(0, 0, 0, 0.12);
        transition: all 0.3s ease;
      }
    }
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 2rem;

    .time-display {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      background: rgba(255, 255, 255, 0.1);
      padding: 0.5rem 1rem;
      border-radius: 12px;
      backdrop-filter: blur(10px);

      .current-time {
        font-weight: 600;
        font-family: 'Monaco', monospace;
      }
    }

    .refresh-btn {
      background: rgba(255, 255, 255, 0.15);
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: white;
      padding: 0.75rem 1.5rem;
      border-radius: 12px;
      backdrop-filter: blur(10px);
      transition: all 0.3s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.25);
        transform: translateY(-2px);
      }

      .spinning {
        animation: spin 1s linear infinite;
      }
    }
  }

// Holiday Banner
.holiday-banner {
  max-width: 1400px;
  margin: 2rem auto 2rem; // Changed from 0 auto 2rem to add top margin
  padding: 0 2rem;

  .holiday-content {
    background: linear-gradient(135deg, #fd7e14 0%, #e67e22 100%);
    border-radius: 20px;
    padding: 1.5rem 2rem;
    display: flex;
    align-items: center;
    gap: 1.5rem;
    color: white;
    position: relative;
    overflow: hidden;

    .holiday-icon {
      font-size: 2.5rem;
      animation: bounce 2s infinite;
    }

    .holiday-text {
      flex: 1;
      text-align: center;
      direction: rtl;

      .holiday-title {
        margin: 0 0 0.5rem;
        font-size: 1.5rem;
        font-weight: 700;
      }

      .holiday-message {
        margin: 0;
        opacity: 0.95;
      }
    }

    .holiday-decoration {
      position: absolute;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;

      .sparkle {
        position: absolute;
        width: 4px;
        height: 4px;
        background: white;
        border-radius: 50%;
        animation: sparkle 3s infinite;

        &:nth-child(1) {
          top: 20%;
          right: 20%;
          animation-delay: 0s;
        }

        &:nth-child(2) {
          top: 60%;
          right: 80%;
          animation-delay: 1s;
        }

        &:nth-child(3) {
          top: 80%;
          right: 40%;
          animation-delay: 2s;
        }
      }
    }
  }
}

// Loading Overlay
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(5px);

  .loading-content {
    text-align: center;

    .loading-spinner {
      width: 60px;
      height: 60px;
      border: 4px solid #f3f3f3;
      border-top: 4px solid var(--brand-red, #0A2634);
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto 1rem;
    }

    .loading-text {
      color: #6c757d;
      font-weight: 500;
      font-size: 1.1rem;
    }
  }
}

// Statistics Grid
.stats-container {
  max-width: 1400px;
  margin: 0 auto 2rem;
  padding: 0 2rem;

  .today-stats-grid {
    margin-bottom: 2rem;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
    gap: 1.5rem;
  }

  .today-stat-card {
    cursor: pointer;
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-6px);
    }
  }

  .today-stat-card-inner {
    border-radius: 24px;
    padding: 1.75rem;
    color: #111827;
    position: relative;
    overflow: hidden;
    background: var(--card-bg, #ffffff);
    box-shadow: 0 10px 35px rgba(17, 24, 39, 0.08);
    border: 1px solid #eef2f7;
    border-left: 5px solid var(--icon-color, #e0111a);

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      border: 1px solid rgba(17, 24, 39, 0.05);
      pointer-events: none;
    }

    .card-footer {
      margin-top: 1.5rem;
      display: block;

      .progress-bar {
        height: 6px;
        background: rgba(17, 24, 39, 0.08);
        border-radius: 999px;
        overflow: hidden;

        .progress-fill {
          height: 100%;
          background: var(--icon-color, #e0111a);
          border-radius: 999px;
          transition: width 0.6s ease;
        }
      }
    }
  }

  .today-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
  }

  .today-card-description {
    margin: 0;
    color: #6b7280;
    font-size: 0.95rem;
  }

  .today-badge {
    background: var(--icon-bg, rgba(224, 17, 26, 0.10));
    padding: 0.25rem 0.75rem;
    border-radius: 999px;
    font-size: 0.85rem;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--icon-color, #e0111a);
  }

  .today-stats-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.25rem;
  }

  .today-stat-block {
    flex: 1;

    small {
      display: block;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      font-weight: 600;
      color: #6b7280;
      margin-bottom: 0.25rem;
    }

    h2 {
      margin: 0;
      font-size: 2.25rem;
      font-weight: 800;
      color: #111827;
    }

    p {
      margin: 0;
      font-size: 1.1rem;
      font-weight: 800;
      color: #111827;
    }
  }

  .today-stat-separator {
    width: 1px;
    height: 64px;
    background: #e5e7eb;
  }

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 1.5rem;

    .stat-card-container {
      cursor: pointer;
      transition: transform 0.3s ease;

      &:hover {
        transform: translateY(-8px);
      }

      .stat-card {
        background: var(--card-bg, #ffffff);
        border-radius: 20px;
        padding: 0;
        box-shadow: 0 10px 35px rgba(17, 24, 39, 0.06);
        border: 1px solid #eef2f7;
        border-left: 5px solid var(--icon-color, #e0111a);
        transition: all 0.3s ease;
        height: 100%;
        color: #111827;
        --card-text-color: #111827;
        --card-text-secondary: #6b7280;

        &:hover {
          box-shadow: 0 14px 50px rgba(17, 24, 39, 0.10);
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.25rem;
          margin-bottom: 0;
          background: rgba(255, 255, 255, 0.65);
          border-bottom: 1px solid #eef2f7;

          .card-left {
            display: flex;
            align-items: center;
            gap: 1rem;
          }

          .card-icon-wrapper {
            width: 46px;
            height: 46px;
            border-radius: 14px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: var(--icon-bg, rgba(224, 17, 26, 0.10));
            border: 1px solid rgba(17, 24, 39, 0.06);

            .card-icon {
              color: var(--icon-color, #e0111a);
            }
          }

          .card-title-header {
            .card-title-inline {
              font-size: 1.1rem;
              font-weight: 700;
              color: #111827;
              margin: 0;
            }
          }
        }

        .card-content {
          padding: 1.1rem 1.25rem 1.25rem 1.25rem;
          margin-bottom: 0;

          .card-stats {
            margin-bottom: 0.75rem;

            .stat-number-row {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 1rem;
              margin-bottom: 0.85rem;
            }

            .stat-amount-row {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 1rem;
            }

            .stat-label {
              color: var(--card-text-secondary, #6b7280);
              font-size: 0.9rem;
              font-weight: 700;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin: 0;
            }

            .stat-number {
              font-size: 1.85rem;
              font-weight: 800;
              color: var(--card-text-color, #1f2937);
              margin: 0;
              line-height: 1;
            }

            .stat-amount {
              color: var(--card-text-secondary, #6b7280);
              font-weight: 800;
              margin: 0;
              font-size: 1.05rem;
              white-space: nowrap;
            }
          }

          .card-info {
            .card-title {
              font-size: 1.1rem;
              font-weight: 600;
              color: var(--card-text-color, #374151);
              margin: 0 0 0.25rem;
            }

            .card-description {
              color: var(--card-text-secondary, #9ca3af);
              font-size: 0.85rem;
              margin: 0;
            }
          }
        }

        .card-footer {
          display: none;
        }
      }
    }
  }
}

// Status-specific styles

// Row 1: Light Backgrounds
.pending-status {
  --card-bg: #FFDAB9;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(255, 140, 0, 0.2);
  --icon-color: #FF8C00;
  --progress-color: rgba(255, 140, 0, 0.6);
}

.attente-collecte-status {
  --card-bg: #F9E79F;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(212, 172, 13, 0.2);
  --icon-color: #D4AC0D;
  --progress-color: rgba(212, 172, 13, 0.6);
}

.waiting-status {
  --card-bg: #D5F5E3;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(40, 167, 69, 0.2);
  --icon-color: #28a745;
  --trend-bg: rgba(40, 167, 69, 0.2);
  --progress-color: rgba(40, 167, 69, 0.8);
}

.collected-status {
  --card-bg: #F7DC6F;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(243, 156, 18, 0.2);
  --icon-color: #F39C12;
  --trend-bg: rgba(243, 156, 18, 0.2);
  --progress-color: rgba(243, 156, 18, 0.8);
}

.depot-status {
  --card-bg: #AED6F1;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(52, 152, 219, 0.2);
  --icon-color: #3498DB;
  --trend-bg: rgba(52, 152, 219, 0.2);
  --progress-color: rgba(52, 152, 219, 0.8);
}

// Row 2: Mixed (En Cours is Light, Others Solid)
.en-cours-status {
  --card-bg: #98FB98;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(34, 139, 34, 0.2);
  --icon-color: #228B22;
  --trend-bg: rgba(34, 139, 34, 0.2);
  --progress-color: rgba(34, 139, 34, 0.8);
}

.delivery-status {
  --card-bg: #FF8C00;
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(255, 140, 0, 0.14);
  --icon-color: #FF8C00;
  --trend-bg: rgba(255, 140, 0, 0.14);
  --progress-color: rgba(255, 140, 0, 0.65);
}

.delivered-status {
  --card-bg: #27AE60;
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(39, 174, 96, 0.14);
  --icon-color: #27AE60;
  --trend-bg: rgba(39, 174, 96, 0.14);
  --progress-color: rgba(39, 174, 96, 0.65);
}

.return-status {
  --card-bg: #E74C3C;
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(231, 76, 60, 0.14);
  --icon-color: #E74C3C;
  --trend-bg: rgba(231, 76, 60, 0.14);
  --progress-color: rgba(231, 76, 60, 0.65);
}

.completed-status {
  --card-bg: #1ABC9C;
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(26, 188, 156, 0.14);
  --icon-color: #1ABC9C;
  --trend-bg: rgba(26, 188, 156, 0.14);
  --progress-color: rgba(26, 188, 156, 0.65);
}

.retour-definitif-status {
  --card-bg: rgba(220, 53, 69, 0.18);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(220, 53, 69, 0.22);
  --icon-color: #DC3545;
  --trend-bg: rgba(220, 53, 69, 0.14);
  --progress-color: rgba(220, 53, 69, 0.75);
}

// Row 3: Solid Darker
.final-return-status {
  --card-bg: #922B21;
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(146, 43, 33, 0.14);
  --icon-color: #922B21;
  --trend-bg: rgba(146, 43, 33, 0.14);
  --progress-color: rgba(146, 43, 33, 0.65);
}

.return-inter-agency-status {
  --card-bg: rgba(192, 57, 43, 0.16);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(192, 57, 43, 0.18);
  --icon-color: #C0392B;
  --trend-bg: rgba(192, 57, 43, 0.12);
  --progress-color: rgba(192, 57, 43, 0.75);
}

.return-inter-agency-received-status {
  --card-bg: rgba(231, 76, 60, 0.16);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(231, 76, 60, 0.18);
  --icon-color: #E74C3C;
  --trend-bg: rgba(231, 76, 60, 0.12);
  --progress-color: rgba(231, 76, 60, 0.75);
}

.transfer-status {
  --card-bg: #C39BD3;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(142, 68, 173, 0.2);
  --icon-color: #8E44AD;
  --trend-bg: rgba(142, 68, 173, 0.2);
  --progress-color: rgba(142, 68, 173, 0.8);
}

.return-billed-status {
  --card-bg: #E59866;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(211, 84, 0, 0.2);
  --icon-color: #D35400;
  --trend-bg: rgba(211, 84, 0, 0.2);
  --progress-color: rgba(211, 84, 0, 0.8);
}

.inter-transfer-status {
  --card-bg: #A04000;
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(160, 64, 0, 0.14);
  --icon-color: #A04000;
  --trend-bg: rgba(160, 64, 0, 0.14);
  --progress-color: rgba(160, 64, 0, 0.65);
}

.received-status {
  --card-bg: #D81B60;
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(216, 27, 96, 0.14);
  --icon-color: #D81B60;
  --trend-bg: rgba(216, 27, 96, 0.14);
  --progress-color: rgba(216, 27, 96, 0.65);
}

.cancelled-status {
  --card-bg: #99A3A4;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(153, 163, 164, 0.16);
  --icon-color: #6B7280;
  --trend-bg: rgba(153, 163, 164, 0.16);
  --progress-color: rgba(153, 163, 164, 0.65);
}

.lost-status {
  --card-bg: #C0392B;
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(192, 57, 43, 0.14);
  --icon-color: #C0392B;
  --trend-bg: rgba(192, 57, 43, 0.14);
  --progress-color: rgba(192, 57, 43, 0.65);
}

.verify-status {
  --card-bg: #F4D03F;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(0, 0, 0, 0.1);
  --icon-color: #000000;
  --trend-bg: rgba(0, 0, 0, 0.1);
  --progress-color: rgba(0, 0, 0, 0.8);
}

.planning-status {
  --card-bg: #D7BDE2;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(142, 68, 173, 0.2);
  --icon-color: #8E44AD;
  --trend-bg: rgba(142, 68, 173, 0.2);
  --progress-color: rgba(142, 68, 173, 0.8);
}

.delivery-anomaly-status {
  --card-bg: #F39C12;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(243, 156, 18, 0.2);
  --icon-color: #E67E22;
  --trend-bg: rgba(243, 156, 18, 0.2);
  --progress-color: rgba(243, 156, 18, 0.8);
}

.a-rembourser-status {
  --card-bg: #F5CBA7;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(230, 126, 34, 0.2);
  --icon-color: #E67E22;
  --trend-bg: rgba(230, 126, 34, 0.2);
  --progress-color: rgba(230, 126, 34, 0.8);
}

.rembourse-status {
  --card-bg: #A9DFBF;
  --card-text-color: #000000;
  --card-text-secondary: #000000;
  --icon-bg: rgba(39, 174, 96, 0.2);
  --icon-color: #27AE60;
  --trend-bg: rgba(39, 174, 96, 0.2);
  --progress-color: rgba(39, 174, 96, 0.8);
}

.livre-temporaire-status {
  --card-bg: #16A085;
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(22, 160, 133, 0.14);
  --icon-color: #16A085;
  --progress-color: rgba(22, 160, 133, 0.65);
}

.retour-temporaire-status {
  --card-bg: #C0392B;
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(192, 57, 43, 0.14);
  --icon-color: #C0392B;
  --progress-color: rgba(192, 57, 43, 0.65);
}

// Summary Section
.summary-section {
  max-width: 1400px;
  margin: 0 auto 2rem;
  padding: 0 2rem;

  .summary-card {
    border: none;
    border-radius: 20px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);

    .summary-content {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .summary-item {
        display: flex;
        align-items: center;
        gap: 0.75rem;

        .summary-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(10, 38, 52, 0.1);
          color: var(--brand-red, #0A2634);

          &.success {
            background: rgba(16, 185, 129, 0.1);
            color: #10b981;
          }

          &.warning {
            background: rgba(245, 158, 11, 0.1);
            color: #f59e0b;
          }

          &.info {
            background: rgba(59, 130, 246, 0.1);
            color: #3b82f6;
          }

          &.error {
            background: rgba(239, 68, 68, 0.1);
            color: #ef4444;
          }
        }

        .summary-data {
          .summary-number {
            font-size: 1.5rem;
            font-weight: 700;
            color: #1f2937;
            margin: 0;
            line-height: 1;
          }

          .summary-label {
            color: #6b7280;
            font-size: 0.85rem;
            margin: 0;
            font-weight: 500;
          }
        }
      }

      .summary-divider {
        width: 1px;
        height: 40px;
        background: #e5e7eb;
      }
    }
  }
}

// Animations
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-10px); }
  60% { transform: translateY(-5px); }
}

@keyframes sparkle {
  0%, 100% { opacity: 0; transform: scale(0); }
  50% { opacity: 1; transform: scale(1); }
}

@keyframes boltFloat {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-4px) scale(1.02); }
}

@keyframes boltGlow {
  0%, 100% { filter: drop-shadow(0 0 10px rgba(255, 214, 102, 0.4)); }
  50% { filter: drop-shadow(0 0 16px rgba(255, 214, 102, 0.9)); }
}

@keyframes boltPulse {
  0%, 100% { opacity: 0.6; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.08); }
}

// Updated Status Styles (White + Color Icons)
.pending-status {
  --card-bg: rgba(189, 189, 189, 0.22);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(158, 158, 158, 0.22);
  --icon-color: #6b7280;
  --progress-color: rgba(107, 114, 128, 0.75);
}

.attente-collecte-status {
  --card-bg: rgba(244, 208, 63, 0.30);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(244, 208, 63, 0.28);
  --icon-color: #D4AC0D;
  --progress-color: rgba(212, 172, 13, 0.75);
}

.waiting-status {
  --card-bg: rgba(56, 142, 60, 0.16);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(56, 142, 60, 0.20);
  --icon-color: #388E3C;
  --trend-bg: rgba(56, 142, 60, 0.12);
  --progress-color: rgba(56, 142, 60, 0.75);
}

.collected-status {
  --card-bg: rgba(0, 173, 181, 0.22);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(0, 173, 181, 0.22);
  --icon-color: #00ADB5;
  --trend-bg: rgba(0, 173, 181, 0.14);
  --progress-color: rgba(0, 173, 181, 0.75);
}

.depot-status {
  --card-bg: rgba(125, 184, 215, 0.28);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(125, 184, 215, 0.24);
  --icon-color: #2f7fb7;
  --trend-bg: rgba(125, 184, 215, 0.14);
  --progress-color: rgba(47, 127, 183, 0.75);
}

.en-cours-status {
  --card-bg: rgba(58, 129, 168, 0.26);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(58, 129, 168, 0.22);
  --icon-color: #3A81A8;
  --trend-bg: rgba(58, 129, 168, 0.14);
  --progress-color: rgba(58, 129, 168, 0.75);
}

.delivery-status {
  --card-bg: rgba(245, 124, 0, 0.16);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(245, 124, 0, 0.20);
  --icon-color: #F57C00;
  --trend-bg: rgba(245, 124, 0, 0.12);
  --progress-color: rgba(245, 124, 0, 0.75);
}

.delivered-status {
  --card-bg: rgba(56, 142, 60, 0.16);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(56, 142, 60, 0.20);
  --icon-color: #388E3C;
  --trend-bg: rgba(56, 142, 60, 0.12);
  --progress-color: rgba(56, 142, 60, 0.75);
}

.return-status {
  --card-bg: rgba(47, 127, 183, 0.26);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(47, 127, 183, 0.22);
  --icon-color: #2f7fb7;
  --trend-bg: rgba(47, 127, 183, 0.14);
  --progress-color: rgba(47, 127, 183, 0.75);
}

.completed-status {
  --card-bg: rgba(0, 137, 123, 0.16);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(0, 137, 123, 0.20);
  --icon-color: #00897B;
  --trend-bg: rgba(0, 137, 123, 0.12);
  --progress-color: rgba(0, 137, 123, 0.75);
}

.retour-definitif-status {
  --card-bg: rgba(220, 53, 69, 0.18);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(220, 53, 69, 0.22);
  --icon-color: #DC3545;
  --trend-bg: rgba(220, 53, 69, 0.14);
  --progress-color: rgba(220, 53, 69, 0.75);
}

.final-return-status {
  --card-bg: rgba(244, 143, 177, 0.28);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(244, 143, 177, 0.22);
  --icon-color: #d32f2f;
  --trend-bg: rgba(244, 143, 177, 0.14);
  --progress-color: rgba(211, 47, 47, 0.75);
}

.transfer-status {
  --card-bg: rgba(142, 36, 170, 0.16);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(142, 36, 170, 0.20);
  --icon-color: #8E24AA;
  --trend-bg: rgba(142, 36, 170, 0.12);
  --progress-color: rgba(142, 36, 170, 0.75);
}

.return-billed-status {
  --card-bg: rgba(220, 53, 69, 0.28);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(220, 53, 69, 0.22);
  --icon-color: #d32f2f;
  --trend-bg: rgba(220, 53, 69, 0.14);
  --progress-color: rgba(211, 47, 47, 0.75);
}

.inter-transfer-status {
  --card-bg: rgba(121, 85, 72, 0.16);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(121, 85, 72, 0.20);
  --icon-color: #795548;
  --trend-bg: rgba(121, 85, 72, 0.12);
  --progress-color: rgba(121, 85, 72, 0.75);
}

.received-status {
  --card-bg: rgba(194, 24, 91, 0.16);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(194, 24, 91, 0.20);
  --icon-color: #C2185B;
  --trend-bg: rgba(194, 24, 91, 0.12);
  --progress-color: rgba(194, 24, 91, 0.75);
}

.cancelled-status {
  --card-bg: rgba(96, 125, 139, 0.16);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(96, 125, 139, 0.20);
  --icon-color: #607D8B;
  --trend-bg: rgba(96, 125, 139, 0.12);
  --progress-color: rgba(96, 125, 139, 0.75);
}

.lost-status {
  --card-bg: rgba(183, 28, 28, 0.16);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(183, 28, 28, 0.20);
  --icon-color: #B71C1C;
  --trend-bg: rgba(183, 28, 28, 0.12);
  --progress-color: rgba(183, 28, 28, 0.75);
}

.planning-status {
  --card-bg: rgba(142, 36, 170, 0.16);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(142, 36, 170, 0.20);
  --icon-color: #8E24AA;
  --trend-bg: rgba(142, 36, 170, 0.12);
  --progress-color: rgba(142, 36, 170, 0.75);
}

.delivery-anomaly-status {
  --card-bg: rgba(243, 156, 18, 0.16);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(243, 156, 18, 0.20);
  --icon-color: #F39C12;
  --trend-bg: rgba(243, 156, 18, 0.12);
  --progress-color: rgba(243, 156, 18, 0.75);
}

.a-rembourser-status {
  --card-bg: rgba(245, 124, 0, 0.16);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(245, 124, 0, 0.20);
  --icon-color: #F57C00;
  --trend-bg: rgba(245, 124, 0, 0.12);
  --progress-color: rgba(245, 124, 0, 0.75);
}

.rembourse-status {
  --card-bg: rgba(56, 142, 60, 0.16);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(56, 142, 60, 0.20);
  --icon-color: #388E3C;
  --trend-bg: rgba(56, 142, 60, 0.12);
  --progress-color: rgba(56, 142, 60, 0.75);
}

.livre-temporaire-status {
  --card-bg: rgba(0, 150, 136, 0.16);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(0, 150, 136, 0.20);
  --icon-color: #009688;
  --progress-color: rgba(0, 150, 136, 0.75);
}

.retour-temporaire-status {
  --card-bg: rgba(211, 47, 47, 0.16);
  --card-text-color: #111827;
  --card-text-secondary: #6b7280;
  --icon-bg: rgba(211, 47, 47, 0.20);
  --icon-color: #D32F2F;
  --progress-color: rgba(211, 47, 47, 0.75);
}

// Dashboard welcome banner
.dashboard-header {
  background:
    radial-gradient(circle at 5% 50%, rgba(255, 255, 255, 0.26) 0 9%, transparent 10%),
    radial-gradient(circle at 78% 18%, rgba(255, 255, 255, 0.16) 0 7%, transparent 8%),
    linear-gradient(135deg, #071b3f 0%, #114092 50%, #2f8ee8 100%) !important;

  max-width: 1400px !important;
  width: 95% !important;
  margin: 2rem auto 0 !important;

  padding: 2rem !important;
  box-shadow: 0 20px 46px rgba(17, 64, 146, 0.24) !important;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 20px !important;
  position: relative;
  overflow: hidden !important;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      linear-gradient(115deg, rgba(255, 255, 255, 0.16) 0 1px, transparent 1px 96px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.12), transparent 42%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    right: -4rem;
    bottom: -5.5rem;
    width: 19rem;
    height: 19rem;
    background: rgba(255, 255, 255, 0.12);
    border-radius: 50%;
    pointer-events: none;
  }

  .header-content {
    position: relative;
    z-index: 1;
    padding: 0 !important;
    gap: 1.5rem;
  }

  .brand-logo-img {
    position: static !important;
    width: 118px;
    max-width: 20vw;
    height: auto;
    filter: drop-shadow(0 18px 28px rgba(3, 19, 45, 0.28));
    transform: rotate(-8deg);
    transition: transform 0.3s ease;
    animation: float3D 6s ease-in-out infinite;

    &:hover {
      animation-play-state: paused;
      transform: rotate(-2deg) scale(1.04);
    }
  }

  @keyframes float3D {
    0%, 100% {
      transform: translateY(0) rotate(-8deg);
    }
    50% {
      transform: translateY(-7px) rotate(-5deg);
    }
  }

  .brand-section {
    min-width: 0;
    gap: 1.25rem;
  }

  .brand-info {
    min-width: 0;
  }

  .brand-title {
    font-size: 2.75rem !important;
    font-weight: 800 !important;
    text-shadow: 0 3px 10px rgba(5, 19, 45, 0.28);
    color: #ffffff !important;
    margin-bottom: 0.5rem !important;
    line-height: 1.2;
    letter-spacing: 0;
    word-break: normal;
  }

  .brand-subtitle {
    color: rgba(255, 255, 255, 0.86) !important;
    font-size: 1.05rem !important;
    font-weight: 500;
    line-height: 1.45;
  }

  .time-display,
  .refresh-btn {
    background: rgba(255, 255, 255, 0.14);
    border: 1px solid rgba(255, 255, 255, 0.28);
    box-shadow: none;
  }
}

// Global "Radius for all coins" (Corners)
.card, .stat-card, .summary-card {
  border-radius: 20px !important;
  overflow: hidden;
}

.avatar, .icon-section, .feather-icon {
  border-radius: 12px !important;
}

// Responsive Design
@media (max-width: 1200px) {
  .stats-grid {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)) !important;
  }
}

@media (max-width: 768px) {
  .dashboard-header {
    padding: 1.5rem !important;

    .header-content {
      flex-direction: column;
      gap: 1.25rem;
      text-align: center;
    }

    .brand-section {
      flex-direction: column;
      gap: 0.75rem;
    }

    .brand-logo-img {
      width: 92px;
      max-width: none;
    }

    .brand-title {
      font-size: 2rem !important;
    }

    .brand-subtitle {
      font-size: 0.95rem !important;
    }

    .header-actions {
      justify-content: center;
      flex-wrap: wrap;
    }
  }

  .stats-grid {
    grid-template-columns: 1fr !important;
  }

  .summary-content {
    flex-direction: column;
    gap: 1.5rem;

    .summary-divider {
      width: 100%;
      height: 1px;
    }
  }

  .holiday-content {
    flex-direction: column;
    text-align: center;

    .holiday-text {
      direction: rtl;
    }
  }
}

@media (max-width: 480px) {
  .modern-dashboard {
    padding: 0;
  }

  .dashboard-header,
  .stats-container,
  .summary-section {
    padding: 0 1rem;
  }

  .dashboard-header {
    padding: 1.25rem 1rem !important;
  }
}
</style>
